/**
 * Admin capstone grading queue — Sprint 5 phase 4 (DEC-0019 / FR-M-2).
 *
 * Lists capstone submissions filtered by status. Per pending row, an
 * "AI Grade" button kicks off /api/admin/capstones/{id}/grade. The
 * grader call takes ~5-15 seconds; the row shows a spinner while the
 * Anthropic API resolves.
 *
 * Once a grade lands, the result panel surfaces the per-criterion
 * scores + rationales, the overall rationale, the confidence band,
 * and the cert issuance status:
 *
 *   - issued_certificate_id present → cert was auto-issued (DEC-0019
 *     high-confidence pass path); link to the verify page.
 *   - cert_issuance_error present → grade landed but cert failed; admin
 *     can retry once the underlying issue is fixed (R2 down, etc.).
 *   - requires_admin_review = true → borderline / low / fail outcome;
 *     a future admin override endpoint flips the final decision.
 *
 * The override + manual cert re-issuance endpoints are not yet wired
 * (Sprint 5 follow-up); this page surfaces the state so the admin can
 * see what they'll need to act on.
 */
import type * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  gradeCapstone,
  listAdminCapstones,
  type CapstoneGradeResult,
  type CapstoneQueueRow,
  type CapstoneStatusFilter,
} from "@/lib/admin-api";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; rows: CapstoneQueueRow[] }
  | { kind: "forbidden" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function confidenceBadgeClass(band: string | null): string {
  if (band === "high") return "bg-emerald-100 text-emerald-900";
  if (band === "borderline") return "bg-amber-100 text-amber-900";
  if (band === "low") return "bg-rose-100 text-rose-900";
  return "bg-muted text-foreground";
}

function passFailBadgeClass(passFail: string | null): string {
  if (passFail === "pass") return "bg-emerald-100 text-emerald-900";
  if (passFail === "fail") return "bg-rose-100 text-rose-900";
  return "bg-muted text-foreground";
}

export function AdminCapstonesPage(): React.JSX.Element {
  const [filter, setFilter] = useState<CapstoneStatusFilter>("submitted");
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [gradingId, setGradingId] = useState<string | null>(null);
  const [gradeResult, setGradeResult] = useState<CapstoneGradeResult | null>(
    null,
  );
  const [gradeError, setGradeError] = useState<string | null>(null);

  const refresh = useCallback(
    async (target: CapstoneStatusFilter): Promise<void> => {
      setLoad({ kind: "loading" });
      try {
        const rows = await listAdminCapstones(target);
        setLoad({ kind: "ready", rows });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToLogin("/admin/capstones");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setLoad({ kind: "forbidden" });
          return;
        }
        const msg = err instanceof Error ? err.message : "Failed to load queue.";
        setLoad({ kind: "error", message: msg });
      }
    },
    [],
  );

  useEffect(() => {
    void refresh(filter);
  }, [filter, refresh]);

  const onGrade = useCallback(
    async (submissionId: string): Promise<void> => {
      setGradingId(submissionId);
      setGradeError(null);
      setGradeResult(null);
      try {
        const result = await gradeCapstone(submissionId);
        setGradeResult(result);
        await refresh(filter);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Grading failed.";
        setGradeError(msg);
      } finally {
        setGradingId(null);
      }
    },
    [filter, refresh],
  );

  if (load.kind === "forbidden") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">
          Admin access required
        </h1>
        <p className="mt-4 text-muted-foreground">
          The capstone grading queue is only available to accounts with
          the admin role. Sign in with an admin account to continue.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <header className="flex items-center justify-between border-b border-border pb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Capstone grading queue
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Triggers Claude Sonnet 4.6 against the published rubric. High-
            confidence passes auto-issue the certificate; borderline / fail
            outcomes wait for admin sign-off.
          </p>
        </div>
        <div className="flex gap-2">
          {(["submitted", "graded", "all"] as CapstoneStatusFilter[]).map(
            (key) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={
                  filter === key
                    ? "rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white"
                    : "rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted"
                }
              >
                {key === "submitted"
                  ? "Pending"
                  : key === "graded"
                    ? "Graded"
                    : "All"}
              </button>
            ),
          )}
        </div>
      </header>

      {gradeResult ? (
        <GradeResultPanel
          result={gradeResult}
          onClose={() => setGradeResult(null)}
        />
      ) : null}

      {gradeError ? (
        <div className="my-6 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          {gradeError}
        </div>
      ) : null}

      <section className="mt-6 overflow-hidden rounded-lg border border-border bg-white shadow-sm">
        {load.kind === "loading" ? (
          <p className="px-6 py-12 text-center text-muted-foreground">Loading…</p>
        ) : load.kind === "error" ? (
          <p className="px-6 py-12 text-center text-rose-700">
            {load.message}
          </p>
        ) : load.kind === "ready" && load.rows.length === 0 ? (
          <p className="px-6 py-12 text-center text-muted-foreground">
            No capstones to show in this filter.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-background">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Candidate
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Attempt
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Words
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Submitted
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Grade
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Confidence
                </th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {load.rows.map((row) => (
                  <tr key={row.submission_id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">
                        {row.user_display_name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {row.user_email}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      #{row.attempt_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {row.word_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {formatDateTime(row.submitted_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.grade_pass_fail ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${passFailBadgeClass(row.grade_pass_fail)}`}
                        >
                          {row.grade_pass_fail.toUpperCase()}{" "}
                          {row.grade_composite}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/80">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {row.grade_confidence_band ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${confidenceBadgeClass(row.grade_confidence_band)}`}
                        >
                          {row.grade_confidence_band}
                        </span>
                      ) : (
                        <span className="text-muted-foreground/80">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.status === "submitted" ? (
                        <button
                          type="button"
                          onClick={() => {
                            void onGrade(row.submission_id);
                          }}
                          disabled={gradingId === row.submission_id}
                          className="rounded-md border border-primary bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-60"
                        >
                          {gradingId === row.submission_id
                            ? "Grading…"
                            : "AI Grade"}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground/80">
                          {row.status}
                        </span>
                      )}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

interface GradeResultPanelProps {
  result: CapstoneGradeResult;
  onClose: () => void;
}

function GradeResultPanel({
  result,
  onClose,
}: GradeResultPanelProps): React.JSX.Element {
  return (
    <section className="my-6 rounded-lg border border-border bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Grade {result.pass_fail === "pass" ? "PASS" : "FAIL"} —{" "}
            {result.composite_score} composite
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Model: {result.grader_reasoning.model} · Confidence:{" "}
            <span
              className={`rounded px-1.5 py-0.5 ${confidenceBadgeClass(result.grader_reasoning.confidence_band)}`}
            >
              {result.grader_reasoning.confidence_band}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>

      {result.issued_certificate_id ? (
        <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <div className="font-semibold">Certificate auto-issued</div>
          <div className="mt-1">
            ID:{" "}
            <a
              href={`/verify/${result.issued_certificate_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono underline"
            >
              {result.issued_certificate_id}
            </a>
          </div>
          <div className="mt-1 text-xs">
            Candidate has been emailed the certificate-issued template.
          </div>
        </div>
      ) : result.cert_issuance_error ? (
        <div className="mt-4 rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          <div className="font-semibold">Cert issuance failed</div>
          <div className="mt-1">{result.cert_issuance_error}</div>
          <div className="mt-1 text-xs">
            The grade is committed; retry issuance once the underlying
            issue is fixed.
          </div>
        </div>
      ) : result.requires_admin_review ? (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-semibold">Admin review required</div>
          <div className="mt-1">
            {result.pass_fail === "pass"
              ? "Pass with low/borderline confidence — review before issuing the cert."
              : "Fail outcome — review feedback before notifying the candidate."}
          </div>
        </div>
      ) : null}

      <h3 className="mt-6 text-sm font-semibold text-foreground">
        Per-criterion
      </h3>
      <div className="mt-2 divide-y divide-border border-y border-border">
        {result.grader_reasoning.criteria.map((c) => (
          <div key={c.id} className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-foreground">
                {c.id.replaceAll("_", " ")}
              </span>
              <span className="text-sm font-semibold text-foreground">
                {c.score} / 5
              </span>
            </div>
            <p className="mt-1 text-sm text-foreground">{c.rationale}</p>
          </div>
        ))}
      </div>

      <h3 className="mt-6 text-sm font-semibold text-foreground">Overall</h3>
      <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">
        {result.grader_reasoning.overall_rationale}
      </p>
    </section>
  );
}
